import React, { useState, useEffect } from 'react';
import { Card, Typography, Row, Col, Button, Form, Input, Select, InputNumber, Switch, message, DatePicker } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import api from '../../services/api';
import CepInput from '../../components/CepInput';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Option } = Select;

const TeamEdit = () => {
  const [teamProfile, setTeamProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [profileForm] = Form.useForm();

  const skillLevels = [
    { value: 'iniciante', label: 'Iniciante' },
    { value: 'recreativo', label: 'Recreativo' },
    { value: 'intermediario', label: 'Intermediário' },
    { value: 'avancado', label: 'Avançado' },
    { value: 'semi_pro', label: 'Semi-pro' }
  ];

  useEffect(() => {
    loadTeamProfile();
  }, []);

  const loadTeamProfile = async () => {
    try {
      const response = await api.get('/community/teams/profile', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const profileData = { ...response.data };
      
      if (profileData.founded_date) {
        try {
          profileData.founded_date = dayjs(profileData.founded_date);
        } catch (dateError) {
          console.warn('Erro ao converter data:', dateError);
          profileData.founded_date = null;
        }
      }
      
      setTeamProfile(response.data);
      profileForm.setFieldsValue(profileData);
    } catch (error) {
      console.error('Erro ao carregar perfil:', error);
      message.error('Erro ao carregar perfil do time');
    }
  };

  const handleUpdateProfile = async (values) => {
    setLoading(true);
    try {
      const formData = { ...values };
      
      if (formData.founded_date) {
        formData.founded_date = formData.founded_date.format('YYYY-MM-DD');
      }
      
      await api.put('/community/teams/profile', formData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      message.success('Perfil atualizado!');
      window.history.back();
    } catch (error) {
      message.error('Erro ao atualizar perfil');
    }
    setLoading(false);
  };

  if (!teamProfile) return <div>Carregando...</div>;

  return (
    <div style={{ padding: '20px' }}>
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24 }}>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => window.history.back()}
            style={{ marginRight: 16 }}
          >
            Voltar
          </Button>
          <Title level={2} style={{ margin: 0 }}>Editar Perfil do Time</Title>
        </div>
        
        <Form
          form={profileForm}
          layout="vertical"
          onFinish={handleUpdateProfile}
        >
          <Row gutter={24}>
            <Col xs={24} md={12}>
              <Form.Item name="court_name" label="Nome da Quadra" rules={[{ required: true }]}>
                <Input placeholder="Nome da quadra/campo" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="skill_level" label="Nível Técnico" rules={[{ required: true }]}>
                <Select placeholder="Selecione o nível">
                  {skillLevels.map(level => (
                    <Option key={level.value} value={level.value}>{level.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col xs={24} md={8}>
              <Form.Item name="guest_price" label="Valor Avulso (R$)">
                <InputNumber style={{ width: '100%' }} placeholder="0.00" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="monthly_price" label="Valor Mensalista (R$)">
                <InputNumber style={{ width: '100%' }} placeholder="0.00" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="search_radius_km" label="Raio de Busca (km)">
                <InputNumber min={1} max={50} style={{ width: '100%' }} placeholder="15" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col xs={24} md={8}>
              <Form.Item name="cep" label="CEP da Quadra" rules={[{ required: true }]}>
                <CepInput
                  value={profileForm.getFieldValue('cep')}
                  onChange={(value) => profileForm.setFieldsValue({ cep: value })}
                  onAddressChange={(addressData) => {
                    profileForm.setFieldsValue({
                      address: addressData.address,
                      city: addressData.city,
                      state: addressData.state,
                      neighborhood: addressData.neighborhood,
                      latitude: addressData.latitude,
                      longitude: addressData.longitude
                    });
                  }}
                  placeholder="00000-000"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={16}>
              <Form.Item name="address" label="Endereço da Quadra" rules={[{ required: true }]}>
                <Input 
                  placeholder="Será preenchido automaticamente"
                  disabled
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col xs={24} md={6}>
              <Form.Item name="city" label="Cidade">
                <Input placeholder="São Paulo" />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item name="state" label="Estado">
                <Input placeholder="SP" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="neighborhood" label="Bairro">
                <Input placeholder="Vila Madalena" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col xs={24} md={12}>
              <Form.Item name="latitude" label="Latitude">
                <InputNumber style={{ width: '100%' }} precision={8} disabled />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="longitude" label="Longitude">
                <InputNumber style={{ width: '100%' }} precision={8} disabled />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="description" label="Descrição do Time">
            <Input.TextArea rows={3} placeholder="Conte sobre o time, horários, local dos jogos..." />
          </Form.Item>

          <Form.Item name="conduct_code" label="Código de Conduta">
            <Input.TextArea rows={2} placeholder="Regras de comportamento do time" />
          </Form.Item>

          <Row gutter={24}>
            <Col xs={24} md={12}>
              <Form.Item name="is_public" label="Perfil Público" valuePropName="checked">
                <Switch checkedChildren="Público" unCheckedChildren="Privado" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="founded_date" label="Data de Fundação">
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" placeholder="Selecione a data" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} size="large" block>
              Salvar Alterações
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default TeamEdit;